/**
 * The block kit's carousel rail, ported into Quil.
 *
 * SOURCE: `app-platform/blocks/cupertino-kit.html`, modules 03, 04 and 07, settled in
 * `app-platform/blocks/REVIEW.md` on 2026-09-19. **The kit is the source and is read-only from
 * here.** If a bug is found in this logic, fix it in the kit first and port the fix outward,
 * exactly like the canonical auth modules — otherwise five apps end up with five subtly different
 * rails, which is the failure `bundle-budget.mjs` already demonstrated.
 *
 * TAKEN FROM CONTENTLY, NOT RE-DERIVED. This file started as a byte copy of
 * `contently-feed-app/src/lib/kitRail.ts` (P4, 2026-09-20), which is the estate's first port of
 * the kit's engine. Quil adds exactly one thing, and it is a piece of the KIT that Contently
 * simply did not need rather than a change of behaviour:
 *
 *   · `[data-prev]` / `[data-next]` transport buttons, which module 07 uses in place of the
 *     autoplay pause button. Same `goTo`, so a click crosses one slide and loops like any other
 *     move. Quil uses 07 and not 04, because a block someone reads at their own pace should not
 *     move under them — the kit's own note on that module, and the right call for a writing app.
 *
 * Anything else that differs between the two copies is drift and should be reconciled, not kept.
 *
 * It is deliberately a near-verbatim port of the kit's vanilla engine rather than a React
 * rewrite. Every line here is the residue of a review round, and a reimplementation is how that
 * gets quietly lost:
 *
 *   · the rail LOOPS by cloning a full set either side and parking in the middle, so it never
 *     reaches an end; the clones are aria-hidden so a reader hears six slides, not eighteen
 *   · DRAG works with a mouse only (touch and pen already scroll natively, and hijacking them
 *     replaces a good gesture with a worse one), with four pixels of slop so an unsteady click is
 *     still a click, and the click that ends a real drag is swallowed in the capture phase so
 *     dragging across a tile does not also navigate to it
 *   · a released drag SETTLES onto a slide, because restoring `scroll-snap-type` does not re-snap
 *     a rail already parked between two
 *   · `scroll-snap-stop: normal` (in the CSS) so one gesture can cross several slides
 *   · a looping rail recycles MID-DRAG, and tells the drag handler how far it moved, or the rail
 *     jumps out from under the cursor
 *
 * WHAT IS NOT PORTED: the kit's demo-only Motion toggle. Its `motionOn` flag becomes the real
 * preference — `motionReduced()`, which is the OS query OR Quil's own in-app Settings switch.
 * Contently's copy reads `matchMedia` alone, and that is a genuine difference rather than drift:
 * Quil has two sources for this preference and the kit has one.
 *
 * WHAT IS ADDED: teardown. The kit runs once on a static page and never unbinds; React mounts and
 * unmounts, and in StrictMode does it twice, so every listener goes through one AbortController
 * and every timer is cleared.
 */

import { motionReduced } from './usePrefersReducedMotion'

export interface RailOptions {
  /** Clone a set either side so the rail never reaches an end. */
  loop?: boolean
  /** Autoplay dwell in ms. 0 or undefined means no autoplay. */
  autoplayMs?: number
}

interface TrackEl extends HTMLElement {
  __loopShift?: (d: number) => void
  __loopNormalise?: () => void
}

/** Mouse drag-to-scroll. Touch and pen already scroll the rail natively. */
function enableDrag(track: TrackEl, signal: AbortSignal, motionOn: () => boolean) {
  let down = false
  let dragging = false
  let startX = 0
  let startScroll = 0
  let pid: number | null = null

  track.addEventListener(
    'pointerdown',
    (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return
      down = true
      dragging = false
      startX = e.clientX
      startScroll = track.scrollLeft
      pid = e.pointerId
    },
    { signal },
  )

  track.addEventListener(
    'pointermove',
    (e) => {
      if (!down || e.pointerId !== pid) return
      const dx = e.clientX - startX
      if (!dragging) {
        // A few pixels of slop, so a click with an unsteady hand is still a click.
        if (Math.abs(dx) < 4) return
        dragging = true
        track.classList.add('dragging')
        // Snapping fights the pointer mid-drag; smooth scrolling animates every assignment, so
        // the rail would lag behind the cursor instead of tracking it. Both come back on release.
        track.style.scrollSnapType = 'none'
        track.style.scrollBehavior = 'auto'
        try {
          track.setPointerCapture(pid)
        } catch {
          /* pointer already gone; capture is a nicety, not a requirement */
        }
      }
      e.preventDefault()
      track.scrollLeft = startScroll - dx
      // A looping rail recycles itself mid-drag; the origin this drag is measured from has to
      // move with it, or the rail jumps out from under the cursor.
      track.__loopNormalise?.()
    },
    { signal },
  )

  track.__loopShift = (d: number) => {
    startScroll += d
  }

  /** Restoring scroll-snap-type does not re-snap a rail already parked between two slides. */
  const settle = () => {
    const slides = track.querySelectorAll<HTMLElement>('.slide')
    if (!slides.length) return
    const first = slides[0]
    if (!first) return
    const base = first.offsetLeft
    const x = track.scrollLeft + base
    let target = base
    let gap = Infinity
    slides.forEach((s) => {
      const d = Math.abs(s.offsetLeft - x)
      if (d < gap) {
        gap = d
        target = s.offsetLeft
      }
    })
    track.scrollTo({ left: target - base, behavior: motionOn() ? 'smooth' : 'auto' })
  }

  const release = () => {
    if (!down) return
    down = false
    if (pid !== null && track.hasPointerCapture?.(pid)) track.releasePointerCapture(pid)
    pid = null
    if (!dragging) return
    track.classList.remove('dragging')
    track.style.scrollSnapType = ''
    track.style.scrollBehavior = ''
    settle()
  }
  track.addEventListener('pointerup', release, { signal })
  track.addEventListener('pointercancel', release, { signal })

  // Swallow the click that ends a real drag, so dragging across a tile does not navigate to it.
  track.addEventListener(
    'click',
    (e) => {
      if (!dragging) return
      dragging = false
      e.preventDefault()
      e.stopPropagation()
    },
    { capture: true, signal },
  )

  track.addEventListener('dragstart', (e) => e.preventDefault(), { signal })
}

/**
 * Wire one `.carousel` element. Returns a teardown function.
 */
export function initRail(el: HTMLElement, opts: RailOptions = {}): () => void {
  const track = el.querySelector<TrackEl>('[data-track]')
  if (!track) return () => {}

  const ac = new AbortController()
  const { signal } = ac
  const timers = new Set<number>()
  const later = (fn: () => void, ms: number) => {
    const t = window.setTimeout(() => {
      timers.delete(t)
      fn()
    }, ms)
    timers.add(t)
    return t
  }

  // The OS query is still needed as an EVENT source, so a preference changed mid-visit is noticed.
  // But the ANSWER comes from motionReduced(), which is the OS query OR Quil's own in-app Settings
  // toggle — reading matchMedia alone ignores the switch Kamrin actually flipped, and the CSS half
  // of that switch (global.css's `:root[data-reduce-motion="on"] *`) hides the gap until some JS
  // decides to smooth-scroll. Found by the P4 Bindr session, 2026-09-22; the divergence from the
  // kit here is Quil having two sources for one preference where the kit has one.
  const reduceQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null
  const motionOn = () => !motionReduced()

  const realSlides = Array.from(track.querySelectorAll<HTMLElement>('.slide'))
  const n = realSlides.length
  const loop = Boolean(opts.loop) && n > 1

  if (loop) {
    const before = document.createDocumentFragment()
    const after = document.createDocumentFragment()
    realSlides.forEach((s) => {
      ;[before, after].forEach((frag) => {
        const c = s.cloneNode(true) as HTMLElement
        c.classList.add('is-clone')
        c.setAttribute('aria-hidden', 'true')
        frag.appendChild(c)
      })
    })
    const firstReal = realSlides[0]
    if (firstReal) track.insertBefore(before, firstReal)
    else track.appendChild(before)
    track.appendChild(after)
  }

  const slides = Array.from(track.querySelectorAll<HTMLElement>('.slide'))
  const dotsBox = el.querySelector<HTMLElement>('[data-dots]')
  const playBtn = el.querySelector<HTMLButtonElement>('[data-playpause]')
  const prevBtn = el.querySelector<HTMLButtonElement>('[data-prev]')
  const nextBtn = el.querySelector<HTMLButtonElement>('[data-next]')
  const status = el.querySelector<HTMLElement>('[data-status]')
  const label = track.getAttribute('aria-label') || 'Carousel'

  const dwell = opts.autoplayMs ?? 0
  const autoplay = dwell > 0

  let index = 0
  let armed = -1
  let userPaused = false
  let touching = false
  let inView = true
  let settleTimer: number | null = null
  const dots: HTMLButtonElement[] = []

  if (dotsBox) {
    dotsBox.setAttribute('data-autoplay', String(autoplay))
    if (autoplay) dotsBox.style.setProperty('--dwell', `${dwell}ms`)
  }

  const paused = () => userPaused || touching || !inView || document.hidden || !motionOn()

  const syncPaused = () => {
    if (dotsBox) dotsBox.setAttribute('data-paused', String(paused()))
    if (playBtn) {
      playBtn.setAttribute('aria-pressed', String(userPaused))
      playBtn.setAttribute('aria-label', userPaused ? 'Play the carousel' : 'Pause the carousel')
    }
  }

  // Scroll offset of a physical slide, measured from the rail's own start. Returns 0 for an
  // out-of-range index rather than throwing: every caller is driven by measured scroll positions,
  // and a rail that is momentarily empty (or mid-teardown) should coast, not crash the page.
  const posOf = (i: number) => {
    const slide = slides[i]
    const first = slides[0]
    if (!slide || !first) return 0
    return slide.offsetLeft - first.offsetLeft
  }
  const setSpan = () => (loop ? posOf(n) : 0)

  /** Keep the rail inside the middle set. The band is loose on purpose so it cannot judder. */
  const normalise = () => {
    if (!loop) return
    const s = setSpan()
    if (!s) return
    let d = 0
    if (track.scrollLeft > s * 2) d = -s
    else if (track.scrollLeft < s * 0.5) d = s
    if (!d) return
    const keep = track.style.scrollBehavior
    track.style.scrollBehavior = 'auto' // a shift must never animate
    track.scrollLeft += d
    track.style.scrollBehavior = keep
    track.__loopShift?.(d)
  }
  if (loop) track.__loopNormalise = normalise

  const nearestPhys = () => {
    const x = track.scrollLeft
    let best = 0
    let gap = Infinity
    for (let i = 0; i < slides.length; i += 1) {
      const d = Math.abs(posOf(i) - x)
      if (d < gap) {
        gap = d
        best = i
      }
    }
    return best
  }

  const paint = () => {
    dots.forEach((d, i) => d.setAttribute('aria-selected', i === index ? 'true' : 'false'))
  }

  const goTo = (i: number, instant?: boolean) => {
    const want = ((i % n) + n) % n
    let target = want
    if (loop) {
      // Three copies of every slide exist; go to whichever is closest, so "next" always means
      // one step forward even when that crosses a set.
      const cur = nearestPhys()
      let bestD = Infinity
      for (let k = 0; k < slides.length; k += 1) {
        if (k % n !== want) continue
        const d = Math.abs(k - cur)
        if (d < bestD) {
          bestD = d
          target = k
        }
      }
    }
    index = want
    track.scrollTo({ left: posOf(target), behavior: instant || !motionOn() ? 'auto' : 'smooth' })
    paint()
  }

  const arm = () => {
    if (!autoplay || !motionOn() || !dots.length) return
    if (armed === index) return
    dots.forEach((d) => d.classList.remove('playing'))
    const dot = dots[index]
    const fill = dot?.querySelector<HTMLElement>('.dot-fill')
    if (!dot || !fill) return
    dot.classList.add('playing')
    fill.style.animation = 'none'
    void fill.offsetWidth // force a reflow so the animation restarts
    fill.style.animation = ''
    armed = index
  }

  const announce = () => {
    if (status) status.textContent = `${label}, item ${index + 1} of ${n}`
  }

  realSlides.forEach((slide, i) => {
    slide.setAttribute('role', 'group')
    slide.setAttribute('aria-roledescription', 'slide')
    slide.setAttribute('aria-label', `${i + 1} of ${n}`)
    if (!dotsBox) return
    const dot = document.createElement('button')
    dot.type = 'button'
    dot.className = 'dot'
    dot.setAttribute('role', 'tab')
    dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false')
    dot.setAttribute('aria-label', `${label}, ${i + 1} of ${n}`)
    dot.innerHTML = '<span class="dot-fill"></span>'
    dot.addEventListener(
      'click',
      () => {
        userPaused = false
        goTo(i)
        syncPaused()
      },
      { signal },
    )
    dotsBox.appendChild(dot)
    dots.push(dot)
  })

  track.addEventListener(
    'scroll',
    () => {
      const i = nearestPhys() % n
      if (i !== index) {
        index = i
        paint()
      }
      if (settleTimer) window.clearTimeout(settleTimer)
      // Recycle once the rail has come to rest; doing it mid-flight would cancel a smooth scroll.
      settleTimer = later(() => {
        normalise()
        arm()
        announce()
      }, 130)
    },
    { passive: true, signal },
  )

  // Slide widths are viewport-relative, so a resize moves every snap point.
  window.addEventListener('resize', () => loop && normalise(), { passive: true, signal })

  track.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'ArrowRight') goTo(index + 1)
      else if (e.key === 'ArrowLeft') goTo(index - 1)
      else return
      e.preventDefault()
      userPaused = false
      syncPaused()
    },
    { signal },
  )

  ;(['pointerdown', 'touchstart'] as const).forEach((ev) =>
    track.addEventListener(
      ev,
      () => {
        touching = true
        syncPaused()
      },
      { passive: true, signal },
    ),
  )
  ;(['pointerup', 'pointercancel', 'touchend', 'touchcancel'] as const).forEach((ev) =>
    window.addEventListener(
      ev,
      () => {
        if (!touching) return
        touching = false
        later(syncPaused, 200)
      },
      { passive: true, signal },
    ),
  )

  playBtn?.addEventListener(
    'click',
    () => {
      userPaused = !userPaused
      syncPaused()
    },
    { signal },
  )

  // Module 07's transport. A manual rail has nothing to pause, so it gets arrows instead — and
  // they are the only way to move it from the keyboard without first focusing the track itself.
  prevBtn?.addEventListener('click', () => goTo(index - 1), { signal })
  nextBtn?.addEventListener('click', () => goTo(index + 1), { signal })

  // Reaching the end of the fill animation is what advances the carousel.
  //
  // The name is matched by SUFFIX, not equality, taken from Contently's copy after that port
  // shipped a bug here: the kit's keyframes are `fillx`, any app will sensibly namespace them, and
  // an equality check against the kit's own name then silently never matches, so autoplay never
  // advances. Nothing looks broken — the dot still fills, once, and stops. Quil's rail is manual
  // so this line is inert here; it is kept identical anyway, because two copies of one engine that
  // differ only where one of them was fixed is how the copies stop being comparable.
  const isFillEnd = (name: string) => name === 'fillx' || name.endsWith('-fillx')
  dotsBox?.addEventListener(
    'animationend',
    (e) => {
      if (!isFillEnd((e as AnimationEvent).animationName) || paused()) return
      goTo(index + 1)
    },
    { signal },
  )

  let io: IntersectionObserver | null = null
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(
      (entries) => {
        const first = entries[0]
        if (!first) return
        inView = first.isIntersecting
        syncPaused()
      },
      { threshold: 0.25 },
    )
    io.observe(el)
  }
  document.addEventListener('visibilitychange', syncPaused, { signal })
  reduceQuery?.addEventListener?.('change', () => {
    if (!motionOn()) {
      dots.forEach((d) => d.classList.remove('playing'))
      armed = -1
    } else {
      arm()
    }
    syncPaused()
  }, { signal })

  enableDrag(track, signal, motionOn)

  // Park a looping rail on the middle set before anything is painted, so there is a whole set of
  // runway in both directions from the first frame.
  if (loop) {
    const keep = track.style.scrollBehavior
    track.style.scrollBehavior = 'auto'
    track.scrollLeft = posOf(n)
    track.style.scrollBehavior = keep
  }

  paint()
  syncPaused()
  arm()
  announce()

  return () => {
    ac.abort()
    io?.disconnect()
    timers.forEach((t) => window.clearTimeout(t))
    timers.clear()
    if (settleTimer) window.clearTimeout(settleTimer)
    // Remove the clones and generated dots so a StrictMode remount does not double them.
    track.querySelectorAll('.slide.is-clone').forEach((c) => c.remove())
    dots.forEach((d) => d.remove())
    delete track.__loopNormalise
    delete track.__loopShift
  }
}
